import { InfoIcon } from '@chakra-ui/icons';
import {
  Badge,
  Card,
  CardBody,
  Flex,
  HStack,
  Heading,
  Hide,
  Icon,
  Skeleton,
  Stack,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import { routes } from '@blockpulse3/data/shared';
import {
  FundraisingStatus,
  SubscriptionWorkflowType,
  useGetFundraisingQuery,
} from '@blockpulse3/graphql/hooks';
import { formatDate } from '@blockpulse3/helpers';
import { CompanyIdentityAvatar, ErrorQueryCard, useErrorToast } from '@blockpulse3/ui/commons';
import { useBadge } from '@blockpulse3/ui/ui-hooks';

type Props = unknown;

/**
 * FundraisingHeader.
 * Header details section of the Fundraising.
 *
 * @returns {JSX.Element}
 */
export function FundraisingHeader(): JSX.Element {
  const i18nFundraisingHeader = useTranslations('FundraisingHeader');
  const i18nFundraising = useTranslations('Fundraising');

  const navigate = useNavigate();
  const { fundraisingId = '', companyId = '' } = useParams();

  const errorToast = useErrorToast();

  const { data, loading, error } = useGetFundraisingQuery({
    variables: {
      fundraisingId,
    },
    onError: () => {
      navigate(routes.workspace.fmt(companyId || ''));
      errorToast({ title: i18nFundraising('Toast.Error') });
    },
  });

  /* ** Fundraising status badge ** */
  const { badge } = useBadge(
    data?.fundraising.status,
    [
      {
        value: FundraisingStatus.CLOSED,
        label: i18nFundraisingHeader('Badge.Closed'),
        color: 'yellow',
      },
      {
        value: FundraisingStatus.STARTED,
        label: i18nFundraisingHeader('Badge.Started'),
        color: 'yellow',
      },
      {
        value: FundraisingStatus.REVIEWED,
        label: i18nFundraisingHeader('Badge.Reviewed'),
        color: 'blue',
      },
      {
        value: FundraisingStatus.FINALIZED,
        label: i18nFundraisingHeader('Badge.Finalized'),
        color: 'green',
      },
    ],
    { label: i18nFundraisingHeader('Badge.Draft'), color: 'gray' },
  );

  if (loading || !fundraisingId) {
    return (
      <Skeleton>
        <Card h="200px" />
      </Skeleton>
    );
  }

  if (error) {
    return <ErrorQueryCard h="200px" />;
  }

  if (!data) {
    return <ErrorQueryCard h="200px" />;
  }

  const fundraising = data.fundraising;

  const isDraft = fundraising.status === FundraisingStatus.DRAFT;
  const isClosed = [
    FundraisingStatus.CLOSED,
    FundraisingStatus.REVIEWED,
    FundraisingStatus.FINALIZED,
  ].includes(fundraising.status);
  const isAllInSubscriptionWorkflow =
    fundraising.subscriptionWorkflowType === SubscriptionWorkflowType.ALL_IN;

  const endDate = fundraising.closingDate || fundraising.endSubscriptionDate;
  const remainingDays = fundraising.endSubscriptionDate
    ? dayjs(fundraising.endSubscriptionDate).diff(dayjs(), 'days')
    : 0;

  const endLabel = isDraft
    ? i18nFundraisingHeader('SubscriptionPeriodTitle')
    : i18nFundraisingHeader('EndIn');

  const tooltipLabel = isDraft
    ? i18nFundraisingHeader('Tooltip.Draft')
    : i18nFundraisingHeader(
        isAllInSubscriptionWorkflow ? 'Tooltip.StartedAllIn' : 'Tooltip.Started',
        {
          date: formatDate(fundraising?.endSubscriptionDate, 'DD MMM YYYY à HH:mm z'),
        },
      );

  const dateLabel = isDraft
    ? i18nFundraisingHeader('SubscriptionPeriod', {
        period: Math.round(fundraising?.subscriptionPeriod / 7),
      })
    : formatDate(endDate, 'DD MMM YYYY');

  return (
    <Card>
      <Flex as={CardBody} direction={{ base: 'column', md: 'row' }}>
        <Hide below="md">
          <CompanyIdentityAvatar boxSize="40" src={fundraising.company.identity?.profilePicture} />
        </Hide>
        <Stack
          alignItems="flex-start"
          justifyContent="center"
          pb={{ base: '4', md: '0' }}
          px={{ base: '0', md: '6' }}
          width="full"
        >
          <Text color="gray.500" fontSize="xs" fontWeight="semibold">
            {i18nFundraisingHeader('CapitalIncrease')}
          </Text>
          <Heading fontWeight="bold">{fundraising.name}</Heading>
          <Badge colorScheme={badge.color}>{badge.label}</Badge>
        </Stack>
        <Flex
          alignItems="flex-end"
          bg="gray.50"
          direction="column"
          flexShrink="0"
          justifyContent="center"
          p="5"
          rounded="sm"
        >
          <HStack>
            <Text color="gray.600" fontWeight="500">
              {endLabel}
            </Text>
            {!isClosed && (
              <Tooltip hasArrow label={tooltipLabel} placement="top">
                <Icon as={InfoIcon} color="gray.600" />
              </Tooltip>
            )}
          </HStack>
          <Text color="gray.600" fontSize="2xl" fontWeight="600">
            {dateLabel}
          </Text>
          {!isDraft && !isClosed && (
            <Text color="gray.600" fontSize="sm" fontWeight="600">
              {i18nFundraisingHeader('RemainingDays', { days: remainingDays })}
            </Text>
          )}
        </Flex>
      </Flex>
    </Card>
  );
}

export type FundraisingHeaderProps = Props;
